import { ComponentProps, ComponentPropsWithoutRef, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useNavigate } from "react-router-dom";
import { ButtonV2, ButtonV2Variant } from "../ButtonV2";

export interface IContainerV2ButtonConfig {
  title?: string;
  icon?: ReactNode;
  onClick?: () => void;
  type?: ComponentProps<"button">["type"];
  disabled?: boolean;
  form?: string;
  /** Which side of the title the icon renders on. Defaults to "left" for the back button and "right" for the next button. */
  iconPosition?: "left" | "right";
  /** `ButtonV2` variant (solid/danger/text/underline/outline/filter). Defaults to "text" for the back button and "solid" for the next button — pick one instead of overriding via `className` when a variant already covers the look you need. */
  variant?: ButtonV2Variant;
  /** Extra classes merged onto the button (e.g. a fixed width for a screen-specific CTA). */
  className?: string;
}

interface IContainerV2Props extends ComponentPropsWithoutRef<"div"> {
  /** `false` hides the back button entirely. Omit for the default (shown, navigates back in history). Pass a config to customize title/icon/action/etc. */
  backButton?: false | IContainerV2ButtonConfig;
  /** Omit to hide. Pass a config to show it — `title` is the only field you'll usually need. */
  nextButton?: IContainerV2ButtonConfig;
}

/**
 * Para que serve: layout raiz de página para os fluxos V2 — centraliza o
 * conteúdo (`max-w-7xl mx-auto`, padding vertical responsivo) e cria a barra
 * de ação no rodapé (botão voltar + botão avançar) que praticamente toda
 * página de um fluxo precisa, para não reimplementar essa barra em cada
 * página.
 *
 * Como funciona: `children` é renderizado no corpo; `backButton`/`nextButton`
 * descrevem os botões do rodapé em vez de você passar os `ButtonV2` prontos.
 * O botão voltar existe por padrão (navega um passo atrás no histórico) —
 * passe `backButton={false}` para escondê-lo, ou um objeto de configuração
 * para customizar título/ícone/ação/variante. O botão avançar só aparece se
 * `nextButton` for informado. Cada botão vira um `ButtonV2` por baixo dos
 * panos, herdando suas variantes (`solid`, `outline`, `text`, `underline`,
 * `danger`, `filter`).
 *
 * Como utilizar: escolha a `variant` do botão (ex.: `variant: "outline"`
 * para o clássico botão contornado) em vez de estilizar via `className` —
 * `className` deve ficar reservado para ajustes que nenhuma variante já
 * cobre (ex.: uma largura fixa específica da tela). Exemplo:
 * ```tsx
 * <ContainerV2
 *   backButton={{ title: "Voltar ao início", variant: "outline", onClick: navigateHome }}
 *   nextButton={{ title: "Avançar", onClick: handleSubmit }}
 * >
 *   {conteudoDaPagina}
 * </ContainerV2>
 * ```
 *
 * Quando utilizar: em qualquer página nova do fluxo V2 que precise da barra
 * padrão de navegação (voltar/avançar) no rodapé. Não é a escolha certa para
 * uma página de confirmação/leitura pura sem nenhuma ação de rodapé — nesse
 * caso, ainda vale usar `ContainerV2` pelo layout centralizado, só omitindo
 * `nextButton` e passando `backButton={false}` quando nem voltar fizer
 * sentido.
 */
export const ContainerV2 = ({
  className,
  children,
  backButton,
  nextButton,
  ...rest
}: IContainerV2Props) => {
  const navigate = useNavigate();

  const showBackButton = backButton !== false;
  const backButtonConfig = backButton || {};
  const backVariant = backButtonConfig.variant ?? "text";
  const showBothButtons = showBackButton && !!nextButton;

  return (
    <div
      {...rest}
      className={twMerge("py-5 w-full max-w-7xl mx-auto md:py-12", className)}
    >
      {children}

      <div
        className={twMerge(
          "mt-10 flex items-center justify-between gap-8 flex-col-reverse md:flex-row",
          !showBothButtons && "justify-center",
        )}
      >
        {showBackButton && (
          <ButtonV2
            className={twMerge(
              backVariant === "text" && "pl-0",
              backButtonConfig.className,
            )}
            variant={backVariant}
            type={backButtonConfig.type ?? "button"}
            disabled={backButtonConfig.disabled}
            form={backButtonConfig.form}
            onClick={backButtonConfig.onClick ?? (() => navigate(-1))}
          >
            {(backButtonConfig.iconPosition ?? "left") === "left" &&
              (backButtonConfig.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-back-red.svg"
                  alt="icon voltar"
                />
              ))}
            {backButtonConfig.title ?? "Voltar"}
            {backButtonConfig.iconPosition === "right" &&
              (backButtonConfig.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-back-red.svg"
                  alt="icon voltar"
                />
              ))}
          </ButtonV2>
        )}

        {nextButton && (
          <ButtonV2
            className={nextButton.className}
            variant={nextButton.variant}
            type={nextButton.type ?? (nextButton.form ? "submit" : "button")}
            disabled={nextButton.disabled}
            form={nextButton.form}
            onClick={nextButton.onClick}
          >
            {nextButton.iconPosition === "left" &&
              (nextButton.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-forward.svg"
                  alt="icon avançar"
                />
              ))}
            {nextButton.title}
            {(nextButton.iconPosition ?? "right") === "right" &&
              (nextButton.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-forward.svg"
                  alt="icon avançar"
                />
              ))}
          </ButtonV2>
        )}
      </div>
    </div>
  );
};
